/**
 * Arma un informe HTML autocontenido para compartir.
 *
 * Un solo archivo, sin dependencias: se abre en cualquier navegador, se manda
 * por mail y se imprime a PDF desde el navegador (tiene hoja de estilo de
 * impresión). Sigue el formato de los informes que ya se le mandaron al cliente.
 *
 * El mapa no va: necesita Leaflet y los tiles online. Queda en la pantalla.
 */
import { LOGO_MQ } from "./logoMq";
import {
  conexion,
  cortesFiltrados,
  fmtCoord,
  fmtDT,
  fmtDur,
  fmtDurHm,
  nf,
  pct,
} from "./cobertura";

function esc(s) {
  return String(s == null ? "" : s).replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c],
  );
}

function plural(n, singular, plural_) {
  return n === 1 ? singular : plural_;
}

function hoy() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function paraNombreArchivo() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const ESTILOS = `
:root{
  --mq-navy-950:#060B17; --mq-slate-700:#2A3145; --mq-slate-400:#9AA0AE;
  --mq-bone-50:#F5F4F0; --mq-orange:#FFA00F; --mq-red:#E93241;
  --mq-success:#6FC06F; --mq-info:#6FA3E0;
  --mq-font-sans:"Geist",-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  --mq-font-mono:"Geist Mono",ui-monospace,"Cascadia Code",Consolas,monospace;
  /* derivados para documento impreso sobre blanco */
  --doc-muted:#5B6472; --doc-line:#E3E1DB; --doc-row:#faf9f7;
  --doc-success-ink:#2f6b2f; --doc-success-bg:#e9f5e9;
  --doc-bad-ink:#a11f2b; --doc-bad-bg:#fdecee;
}
*{box-sizing:border-box}
html,body{margin:0}
body{background:#fff;color:var(--mq-navy-950);font-family:var(--mq-font-sans);font-size:13.5px;line-height:1.55;-webkit-font-smoothing:antialiased}
.page{max-width:820px;margin:0 auto;padding:34px 40px 60px}
.mono{font-family:var(--mq-font-mono);font-variant-numeric:tabular-nums}
header.doc{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;border-bottom:2px solid var(--mq-navy-950);padding-bottom:16px}
header.doc .logo svg{height:34px;width:auto;display:block}
header.doc .meta{text-align:right;font-family:var(--mq-font-mono);font-size:10px;color:var(--doc-muted);letter-spacing:.04em;line-height:1.7}
h1{font-size:22px;font-weight:500;letter-spacing:-.02em;margin:22px 0 2px}
.subtitle{color:var(--doc-muted);font-size:13px;margin-bottom:4px}
h2{font-size:14px;font-weight:600;letter-spacing:-.01em;margin:28px 0 8px;padding-bottom:5px;border-bottom:1px solid var(--doc-line)}
h2 .n{display:inline-block;width:20px;height:20px;line-height:20px;text-align:center;background:var(--mq-navy-950);color:#fff;border-radius:5px;font-size:11px;font-family:var(--mq-font-mono);margin-right:8px;vertical-align:1px}
p{margin:8px 0}
.cards{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:14px 0}
@media(max-width:680px){.cards{grid-template-columns:1fr}}
.card{border:1px solid var(--doc-line);border-radius:9px;padding:13px 15px;background:#fff;border-top:3px solid var(--doc-line)}
.card .cap{font-family:var(--mq-font-mono);font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:var(--doc-muted)}
.card .alias{font-size:15px;font-weight:500;letter-spacing:-.01em;margin-top:2px}
.card .alias .pat{font-family:var(--mq-font-mono);font-size:11px;font-weight:400;opacity:.65;margin-left:6px}
.card .big{font-size:30px;font-weight:500;letter-spacing:-.02em;margin:8px 0 0;font-variant-numeric:tabular-nums}
.card .big small{font-size:12px;font-weight:400;color:var(--doc-muted);letter-spacing:0;margin-left:4px}
.card dl{margin:9px 0 0;display:grid;grid-template-columns:1fr auto;gap:4px 10px;font-size:12px}
.card dt{color:var(--doc-muted)}
.card dd{margin:0;text-align:right;white-space:nowrap;font-family:var(--mq-font-mono);font-variant-numeric:tabular-nums}
.integridad{margin-top:11px;padding:7px 10px;border-radius:6px;font-size:11.5px;line-height:1.4}
.integridad.ok{background:var(--doc-success-bg);color:var(--doc-success-ink)}
.integridad.falta{background:var(--doc-bad-bg);color:var(--doc-bad-ink)}
table{width:100%;border-collapse:collapse;font-size:12.5px;margin:12px 0}
th{font-family:var(--mq-font-mono);font-size:9.5px;text-transform:uppercase;letter-spacing:.05em;color:var(--doc-muted);text-align:right;padding:7px 8px;border-bottom:1.5px solid var(--mq-navy-950);font-weight:500;vertical-align:bottom}
th:first-child{text-align:left}
td{padding:7px 8px;border-bottom:1px solid var(--doc-line);text-align:right;font-variant-numeric:tabular-nums}
td:first-child{text-align:left;font-weight:500}
td .pat{font-family:var(--mq-font-mono);font-size:10.5px;font-weight:400;opacity:.65;display:block}
tr:nth-child(even) td{background:var(--doc-row)}
figure{margin:18px 0;break-inside:avoid}
figcaption{font-weight:600;font-size:13px;margin:0 0 8px}
.duo{display:grid;grid-template-columns:1fr 1fr;gap:18px}
@media(max-width:680px){.duo{grid-template-columns:1fr}}
.note{color:var(--doc-muted);font-size:11.5px;margin-top:4px}
.legend{display:flex;gap:16px;flex-wrap:wrap;font-size:12px;color:var(--doc-muted);margin-top:6px}
.legend i{display:inline-block;width:12px;height:12px;border-radius:2px;vertical-align:-2px;margin-right:6px}
footer.doc{margin-top:34px;padding-top:12px;border-top:1px solid var(--doc-line);font-family:var(--mq-font-mono);font-size:9.5px;color:var(--doc-muted);letter-spacing:.04em;display:flex;justify-content:space-between;flex-wrap:wrap;gap:8px}
/* Los SVG vienen de la pantalla con estas clases; acá se les repone el estilo. */
svg.cg-chart{width:100%;height:auto;display:block;overflow:visible}
.cg-grid{stroke:var(--doc-line);stroke-width:1}
.cg-ax,.cg-axv{font-family:var(--mq-font-mono);font-size:9px;fill:var(--doc-muted)}
.cg-lane{font-family:var(--mq-font-mono);font-size:10px;letter-spacing:.06em;fill:var(--doc-muted)}
@media print{
  @page{size:A4;margin:16mm}
  body{font-size:11pt}
  .page{max-width:none;margin:0;padding:0}
  h2{break-after:avoid}
  table,.cards,figure,.card{break-inside:avoid}
}
`;

function tarjeta(u) {
  const c = conexion(u);
  const equipo = [
    u.equipo ? `Equipo ${u.equipo}` : "Equipo sin identificar",
    u.equipoRed,
    u.chipEmpresa,
  ]
    .filter(Boolean)
    .join(" · ");

  return `
  <div class="card" style="border-top-color:${esc(u.hex)}">
    <div class="cap">${esc(equipo)}</div>
    <div class="alias">${esc(u.alias)}<span class="pat">${esc(u.patente)}</span></div>
    <div class="big">${esc(pct(c.enVivoPct))}<small>en vivo</small></div>
    <dl>
      <dt>Cortes de conexión*</dt><dd>${esc(nf(c.cortes))}</dd>
      <dt>El más largo</dt><dd>${c.masLargoMin ? esc(fmtDurHm(c.masLargoMin)) : "—"}</dd>
      <dt>Posiciones</dt><dd>${esc(nf(c.posiciones))}</dd>
      <dt>Con demora</dt><dd>${esc(pct(c.conDemoraPct))}</dd>
    </dl>
    <div class="integridad ${c.integro ? "ok" : "falta"}">
      ${
        c.integro
          ? "Información completa: el recorrido llegó entero al reconectarse."
          : `${esc(nf(c.sinRecuperar))} ${plural(c.sinRecuperar, "corte", "cortes")} sin recuperar: falta el recorrido de ${plural(c.sinRecuperar, "ese tramo", "esos tramos")}.`
      }
    </div>
  </div>`;
}

function filaComparativa(u) {
  const c = conexion(u);
  return `
  <tr>
    <td>${esc(u.alias)}<span class="pat">${esc(u.patente)}</span></td>
    <td>${esc(u.equipo || "—")}${u.equipoRed ? " · " + esc(u.equipoRed) : ""}</td>
    <td>${esc(u.chipEmpresa || "—")}</td>
    <td>${esc(pct(c.enVivoPct))}</td>
    <td>${esc(nf(c.cortes))}</td>
    <td>${c.masLargoMin ? esc(fmtDurHm(c.masLargoMin)) : "—"}</td>
    <td>${esc(nf(c.posiciones))}</td>
    <td>${esc(pct(c.conDemoraPct))}</td>
  </tr>`;
}

function filasCortesSenal(unidades, minFiltro) {
  const filas = [];
  unidades.forEach((u) => {
    cortesFiltrados(u, minFiltro).forEach((c) => {
      filas.push({ u, c, orden: c.t0 });
    });
  });
  filas.sort((a, b) => a.orden - b.orden);
  if (!filas.length) {
    return `<tr><td colspan="6">Ningún corte de señal supera los ${esc(minFiltro)} minutos en el período.</td></tr>`;
  }
  return filas
    .map(
      ({ u, c }) => `
  <tr>
    <td>${esc(u.alias)}<span class="pat">${esc(u.patente)}</span></td>
    <td>${esc(fmtDT(c.ini))}</td>
    <td>${esc(fmtDT(c.fin))}</td>
    <td>${esc(fmtDur(c.dur))}</td>
    <td>${esc(c.estado)}</td>
    <td class="mono">${esc(fmtCoord(c.lat, c.lon))}</td>
  </tr>`,
    )
    .join("");
}

/**
 * @param {object} datos
 * @param {Array}  datos.unidades  unidades parseadas
 * @param {Array}  datos.graficos  outerHTML de los SVG que ya están en pantalla
 * @param {number} datos.minFiltro umbral del filtro de cortes de señal
 */
export function construirInformeHtml({ unidades, graficos, minFiltro }) {
  if (!unidades.length) return "";

  const u0 = unidades[0];
  const periodo = `${fmtDT(u0.desde)} — ${fmtDT(u0.hasta)}`;
  const cliente = u0.cliente || "—";
  const alias = unidades.map((u) => u.alias).join(" · ");
  const totalPos = unidades.reduce((acc, u) => acc + conexion(u).posiciones, 0);
  const conHuecos = unidades.filter((u) => !conexion(u).integro);

  const leyenda = unidades
    .map((u) => `<span><i style="background:${esc(u.hex)}"></i>${esc(u.alias)}</span>`)
    .join("");

  const titulos = [
    "Cobertura de señal, día por día",
    "Línea de tiempo de cortes de señal",
    "Duración de los cortes de señal",
    "Cortes de señal por hora del día",
  ];
  const figura = (svg, i) =>
    `<figure><figcaption>${esc(titulos[i] || "Gráfico")}</figcaption>${svg}<div class="legend">${leyenda}</div></figure>`;

  // Los dos primeros son anchos; los de barras se dibujan para media página y
  // estirados al ancho completo quedan desproporcionados.
  const anchos = graficos.slice(0, 2).map(figura).join("");
  const angostos = graficos.slice(2).map((s, i) => figura(s, i + 2)).join("");
  const figuras = anchos + (angostos ? `<div class="duo">${angostos}</div>` : "");

  return `<!DOCTYPE html>
<html lang="es-AR">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>mobileQuest · Cobertura GSM · ${esc(alias)}</title>
<style>${ESTILOS}</style>
</head>
<body>
<div class="page">
  <header class="doc">
    <div class="logo">${LOGO_MQ}</div>
    <div class="meta">
      INFORME DE COBERTURA GSM<br>
      Cliente: ${esc(cliente)}<br>
      Unidades: ${esc(alias)}<br>
      Emisión: ${esc(hoy())}<br>
      Análisis: ${esc(periodo)}
    </div>
  </header>

  <h1>Cobertura GSM y pérdidas de señal</h1>
  <p class="subtitle">
    Análisis posición por posición de ${esc(nf(unidades.length))}
    ${plural(unidades.length, "unidad", "unidades")} ·
    ${esc(nf(totalPos))} posiciones analizadas
  </p>

  <h2><span class="n">1</span>Cómo reportó cada unidad</h2>
  <div class="cards">${unidades.map(tarjeta).join("")}</div>
  <p class="note">
    * Corte de conexión: más de 16 minutos sin transmitir (con la unidad en marcha o detenida, el
    equipo reporta cada 8 minutos como máximo). <b>Con demora</b>: posiciones que el equipo no pudo
    enviar en el momento y llegaron al reconectarse.
  </p>
  <p class="note">
    ${
      conHuecos.length
        ? `<b>Atención:</b> ${esc(conHuecos.map((u) => u.alias).join(", "))} tiene(n) cortes cuyo recorrido no llegó: en esos tramos falta información.`
        : "En todos los cortes el equipo guardó el recorrido y lo envió al reconectarse: no falta información."
    }
  </p>

  <h2><span class="n">2</span>Comparativa</h2>
  <table>
    <thead><tr>
      <th>Unidad</th><th>Equipo</th><th>Telefonía</th><th>En vivo</th>
      <th>Cortes</th><th>El más largo</th><th>Posiciones</th><th>Con demora</th>
    </tr></thead>
    <tbody>${unidades.map(filaComparativa).join("")}</tbody>
  </table>

  <h2><span class="n">3</span>Gráficos</h2>
  ${figuras}

  <h2><span class="n">4</span>Detalle de cortes de señal</h2>
  <p class="note">
    Pares de alertas 107 (pérdida) y 108 (reenganche), de más de ${esc(minFiltro)} minutos. Es una
    medición distinta de los cortes de conexión de la sección 1.
  </p>
  <table>
    <thead><tr>
      <th>Unidad</th><th>Inicio</th><th>Fin / reenganche</th><th>Duración</th>
      <th>Estado</th><th>Dónde perdió señal</th>
    </tr></thead>
    <tbody>${filasCortesSenal(unidades, minFiltro)}</tbody>
  </table>

  <footer class="doc">
    <span>mobileQuest · Monitoreo satelital de flotas</span>
    <span>Generado el ${esc(hoy())}</span>
  </footer>
</div>
</body>
</html>`;
}

/** Arma el informe y lo baja como archivo. */
export function descargarInforme(datos) {
  const html = construirInformeHtml(datos);
  if (!html) return false;

  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `informe-cobertura-gsm-${paraNombreArchivo()}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}
